import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Upload, Button, Input, message, Spin } from 'antd'; // 引入 Spin
import { PlusOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { uploadLocalImage } from '@/services/xiaohongshu';
import ImageEditor from './imageEditor';
import {
  EditingContextProvider,
  useEditingContext,
} from './editingContextManager';

interface ImageWallProps {
  imgs: string[];
  onChange: (newImgs: string[]) => void;
}

const { TextArea } = Input;

const SortableItem = SortableElement(
  ({
    image,
    index,
    onDelete,
    onPreview,
    isGenerating,
    editingContextManager, // 保留：上下文管理器
  }: {
    image: string;
    index: number;
    onDelete: (image: string) => void;
    onPreview: (image: string) => void;
    isGenerating: boolean;
    editingContextManager: any;
  }) => {
    const handlePreview = () => {
      onPreview(image);
    };

    return (
      <Col span={4} style={{ marginBottom: 16, position: 'relative' }}>
        <div
          style={{
            position: 'relative',
            display: 'inline-block',
            width: 128,
            height: 128,
            overflow: 'hidden',
            cursor: 'pointer',
            borderRadius: '4px',
            border: '2px solid transparent',
          }}
          onMouseEnter={(e) => {
            const overlay = e.currentTarget.querySelector(
              '.overlay',
            ) as HTMLElement;
            if (overlay) {
              overlay.style.opacity = '0.8';
            }
          }}
          onMouseLeave={(e) => {
            const overlay = e.currentTarget.querySelector(
              '.overlay',
            ) as HTMLElement;
            if (overlay) {
              overlay.style.opacity = '0';
            }
          }}
        >
          <img
            alt="image"
            src={image}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />

          {/* 生成中遮罩 */}
          {isGenerating && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: 'white',
                fontSize: '12px',
              }}
            >
              <Spin size="small" />
              <div style={{ marginTop: '8px' }}>正在生成</div>
            </div>
          )}

          {/* 后台生成时显示已用时间 */}
          {(() => {
            const context = editingContextManager.getContext(image);
            if (context?.isGenerating) {
              const elapsedTime = editingContextManager.getElapsedTime(image);
              return (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '4px',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                  }}
                >
                  {`${elapsedTime} s`}
                </div>
              );
            }
            return null;
          })()}

          {/* 蒙版，鼠标悬停时显示 */}
          <div
            className="overlay"
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              height: '30%',
              width: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              opacity: 0,
              transition: 'opacity 0.3s',
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              color: 'white',
              fontSize: '20px',
              flexDirection: 'row',
            }}
          >
            <Button
              icon={<EyeOutlined />}
              type="link"
              onClick={handlePreview}
              style={{ color: 'white' }}
            />
            <Button
              icon={<DeleteOutlined />}
              type="link"
              onClick={() => onDelete(image)}
              style={{ color: 'red' }}
            />
          </div>
        </div>
      </Col>
    );
  },
);

const SortableList = SortableContainer(
  ({
    images,
    onDelete,
    onUpload,
    onPreview,
    generatingImages,
    editingContextManager,
  }: {
    images: string[];
    onDelete: (image: string) => void;
    onUpload: (file: any) => Promise<boolean>;
    onPreview: (image: string) => void;
    generatingImages: Set<string>;
    editingContextManager: any;
  }) => {
    return (
      <Row gutter={[16, 16]}>
        <Col span={4} style={{ marginBottom: 16, position: 'relative' }}>
          <Upload
            customRequest={async ({ file }) => {
              await onUpload(file);
            }}
            showUploadList={false}
            beforeUpload={(file) => {
              const isJpgOrPng =
                file.type === 'image/jpeg' ||
                file.type === 'image/png' ||
                file.type === 'image/webp';
              if (!isJpgOrPng) {
                message.error('只能上传 JPG/PNG/WEBP 格式的图片!');
              }
              const isLt25M = file.size / 1024 / 1024 < 25;
              if (!isLt25M) {
                message.error('图片大小不能超过 25MB!');
              }
              return isJpgOrPng && isLt25M;
            }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 128,
                height: 128,
                overflow: 'hidden',
                cursor: 'pointer',
                border: '1px dashed #d9d9d9',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>上传</div>
            </div>
          </Upload>
        </Col>
        {images.map((image, index) => (
          <SortableItem
            key={image}
            index={index}
            image={image}
            onDelete={onDelete}
            onPreview={onPreview}
            isGenerating={generatingImages.has(image)}
            editingContextManager={editingContextManager}
          />
        ))}
      </Row>
    );
  },
);

const ImageWallContent: React.FC<ImageWallProps> = ({ imgs, onChange }) => {
  const editingContextManager = useEditingContext();

  const [uploading, setUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [generatingImages, setGeneratingImages] = useState<Set<string>>(
    new Set(),
  );
  const [imageModifications, setImageModifications] = useState<
    Map<string, string[]>
  >(new Map());

  // 使用 useCallback 稳定函数引用
  const checkGenerationResults = useCallback(() => {
    // 检查所有图片的生成结果
    for (const imageUrl of imgs) {
      const result = editingContextManager.getGenerationResult(imageUrl);
      if (result) {
        console.log('ImageWall - 发现生成结果:', imageUrl, result);

        if (result.type === 'success') {
          message.success(`图片 "${imageUrl.split('/').pop()}" 生成成功！`);

          // 更新生成状态
          setGeneratingImages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(imageUrl);
            return newSet;
          });

          // 可以选择性地添加生成的图片到修改历史
          if (result.data.resp && Array.isArray(result.data.resp)) {
            const context = editingContextManager.getContext(imageUrl);
            if (context) {
              setImageModifications((prev) => {
                const newMap = new Map(prev);
                const currentModified = newMap.get(imageUrl) || [imageUrl];
                newMap.set(imageUrl, [...currentModified, ...result.data.resp]);
                return newMap;
              });
            }
          }
        } else if (result.type === 'error') {
          message.error(`图片 "${imageUrl.split('/').pop()}" 生成失败，请重试`);

          // 更新生成状态
          setGeneratingImages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(imageUrl);
            return newSet;
          });
        }

        // 清除结果避免重复处理
        editingContextManager.clearGenerationResult(imageUrl);
      }
    }
  }, [imgs, editingContextManager]);

  const checkGeneratingStates = useCallback(() => {
    const newGeneratingImages = new Set<string>();

    // 检查所有图片的生成状态
    imgs.forEach((imageUrl) => {
      const context = editingContextManager.getContext(imageUrl);
      if (context?.isGenerating) {
        newGeneratingImages.add(imageUrl);
      }
    });

    // 只有当状态真正改变时才更新
    setGeneratingImages((prev) => {
      if (prev.size !== newGeneratingImages.size) {
        return newGeneratingImages;
      }

      for (const imageUrl of newGeneratingImages) {
        if (!prev.has(imageUrl)) {
          return newGeneratingImages;
        }
      }

      return prev;
    });
  }, [imgs, editingContextManager]);

  // 监听生成结果并发送通知
  useEffect(() => {
    // 立即检查一次
    checkGenerationResults();

    // 定期检查结果
    const interval = setInterval(checkGenerationResults, 1000);

    return () => clearInterval(interval);
  }, [checkGenerationResults]);

  // 监听生成状态变化，更新本地生成状态
  useEffect(() => {
    // 立即检查一次
    checkGeneratingStates();

    // 定期检查状态变化
    const interval = setInterval(checkGeneratingStates, 1000);

    return () => clearInterval(interval);
  }, [checkGeneratingStates]);

  const handlePreview = (image: string) => {
    // 尝试切换到已有上下文，如果没有则创建新的
    let context = editingContextManager.getContext(image);
    if (!context) {
      editingContextManager.createContext(image, {
        modifiedImages: imageModifications.get(image) || [image],
      });
      context = editingContextManager.getContext(image)!;
    } else {
      editingContextManager.switchContext(image);
    }

    setPreviewImage(image);
    setPreviewVisible(true);
  };

  const handleGeneratingChange = (image: string, isGenerating: boolean) => {
    setGeneratingImages((prev) => {
      const newSet = new Set(prev);
      if (isGenerating) {
        newSet.add(image);
      } else {
        newSet.delete(image);
      }
      return newSet;
    });

    // 更新上下文的生成状态
    editingContextManager.updateContext(image, {
      isGenerating,
      elapsedTime: isGenerating
        ? 0
        : editingContextManager.getContext(image)?.elapsedTime || 0,
    });
  };

  // 处理 Modal 关闭
  const handleEditorCancel = () => {
    const context = editingContextManager.getContext(previewImage);

    if (context?.isGenerating) {
      // 如果正在生成，保持上下文但关闭Modal
      // 后台任务会继续运行
    }

    setPreviewVisible(false);
  };

  const handleModifiedImagesChange = (
    image: string,
    modifiedImages: string[],
  ) => {
    setImageModifications((prev) => {
      const newMap = new Map(prev);
      newMap.set(image, modifiedImages);
      return newMap;
    });
  };

  // 优化：清理不再存在图片的编辑历史
  useEffect(() => {
    setImageModifications((prev) => {
      const newMap = new Map(prev);
      // 清理不在当前图片列表中的编辑历史
      for (const [imageUrl] of newMap) {
        if (!imgs.includes(imageUrl)) {
          newMap.delete(imageUrl);
        }
      }
      return newMap;
    });
  }, [imgs]);

  const handleConfirmEdit = (originalImage: string, newImage: string) => {
    // 找到原始图片在数组中的位置并替换
    const newImgs = imgs.map((img) => (img === originalImage ? newImage : img));
    onChange(newImgs);
    message.success('图片修改已确认！');
  };

  const handleImageUpload = async (file: any): Promise<boolean> => {
    if (uploading) {
      message.warning('已有文件正在上传，请稍候...');
      return false;
    }
    setUploading(true);
    message.loading({ content: '图片上传中...', key: 'uploading' });

    try {
      const response: any = await uploadLocalImage({
        file: file as File, // 确保 file 是 File 类型
      });
      if (response['resp']['code'] === 0) {
        const ossUrl = response.resp.url; // 根据你的后端响应调整
        const newImgs = [ossUrl, ...imgs];
        onChange(newImgs);
        message.success({
          content: '图片上传成功!',
          key: 'uploading',
          duration: 2,
        });
        setUploading(false);
        return true;
      } else {
        setUploading(false);
        alert('图片上传失败，请稍后重试');
        return false;
      }
    } catch (error) {
      setUploading(false);
      alert('图片上传失败，请稍后重试');
      return false;
    }
  };

  const handleDeleteImage = (imageToDelete: string) => {
    // 注意：如果这些图片是持久化存储在 OSS 上的，
    // 你可能也需要一个后端接口来处理从 OSS 删除图片的操作。
    // 前端直接删除只是从当前列表移除了 URL。
    const newImgs = imgs.filter((img) => img !== imageToDelete);
    onChange(newImgs);
  };

  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const newImgs = [...imgs];
    const [movedImage] = newImgs.splice(oldIndex, 1);
    newImgs.splice(newIndex, 0, movedImage);
    onChange(newImgs);
  };

  return (
    <div>
      <SortableList
        images={imgs}
        onUpload={handleImageUpload}
        onDelete={handleDeleteImage}
        onPreview={handlePreview}
        onSortEnd={handleSortEnd}
        generatingImages={generatingImages}
        editingContextManager={editingContextManager}
        axis="xy"
      />
      {previewVisible && (
        <ImageEditor
          visible={previewVisible}
          image={previewImage}
          images={imgs.filter((img) => img !== previewImage)}
          onCancel={handleEditorCancel}
          onConfirm={handleConfirmEdit}
          onGeneratingChange={(isGenerating) =>
            handleGeneratingChange(previewImage, isGenerating)
          }
          onModifiedImagesChange={handleModifiedImagesChange}
          onClearParentSelection={() => {
            // 不再需要清空选中状态
          }}
          // savedModifiedImages={
          //   imageModifications.get(previewImage) || [previewImage]
          // }
          // isGenerating={
          //   editingContextManager.getContext(previewImage)?.isGenerating ||
          //   false
          // }
          // savedElapsedTime={
          //   editingContextManager.getContext(previewImage)?.elapsedTime || 0
          // }
        />
      )}
    </div>
  );
};

// 主组件包装器，提供上下文
const ImageWall: React.FC<ImageWallProps> = ({ imgs, onChange }) => {
  return (
    <EditingContextProvider>
      <ImageWallContent imgs={imgs} onChange={onChange} />
    </EditingContextProvider>
  );
};

export default ImageWall;
